import type {
  ContextualIssue,
  ContextualMergeRequest,
  GetUserContextualWorkItemsData,
} from '@gitlab-org/graphql';
import { DEFAULT_WELCOME_CATEGORIES } from '@gitlab-org/tui';
import type { WelcomePrompt, WelcomePromptCategory, WelcomePromptSegment } from '@gitlab-org/tui';

const PROMPTS_PER_CATEGORY = 4;

const RECENT_ACTIVITY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const DISCUSSION_HEAVY_NOTES_THRESHOLD = 3;

/**
 * Start of the recency window, for the `updatedAfter` variable of
 * `GetUserContextualWorkItemsQuery`: the window is applied server-side so the
 * query doesn't return issues these heuristics would only discard.
 * `recentIssues` still re-checks it locally, which keeps the heuristics correct
 * for any caller and costs nothing on the query's capped node list.
 */
export const recentActivityCutoff = (): string =>
  new Date(Date.now() - RECENT_ACTIVITY_WINDOW_MS).toISOString();

/**
 * Builds a segmented contextual prompt so the welcome card can style the
 * reference (`!5` / `#7`) and the work item title distinctly from the action
 * text — no delimiters needed (titles routinely contain brackets themselves).
 * Title segments are display-only; the card omits them from the submitted
 * prompt (see `welcomePromptRunText`).
 */
const contextualPrompt = (
  before: string,
  prefix: '!' | '#',
  item: { iid: string; title: string },
  after = '',
): WelcomePrompt => {
  const segments: WelcomePromptSegment[] = [
    { kind: 'text', text: before },
    { kind: 'reference', text: `${prefix}${item.iid}` },
    { kind: 'title', text: ` ${item.title}` },
  ];
  if (after) segments.push({ kind: 'text', text: after });
  return segments;
};

type MergeRequestWithRole = {
  mergeRequest: ContextualMergeRequest;
  /** The user authored the MR (as opposed to only being assigned to it). */
  isAuthor: boolean;
};

const byUpdatedAtDesc = (a: { updatedAt: string }, b: { updatedAt: string }): number =>
  Date.parse(b.updatedAt) - Date.parse(a.updatedAt);

/**
 * `resolvableDiscussionsCount`/`resolvedDiscussionsCount` are absent on
 * GitLab instances older than 17.7 — treat "unknown" as "no unresolved
 * discussions" rather than guessing.
 */
const hasUnresolvedDiscussions = (mergeRequest: ContextualMergeRequest): boolean =>
  typeof mergeRequest.resolvableDiscussionsCount === 'number' &&
  typeof mergeRequest.resolvedDiscussionsCount === 'number' &&
  mergeRequest.resolvableDiscussionsCount > mergeRequest.resolvedDiscussionsCount;

/**
 * Every contextual prompt a single MR qualifies for, highest priority first,
 * or empty when no signal applies. Only the first is used while distinct MRs
 * are still available; the rest are last-resort filler (see
 * {@link preferDistinctItems}).
 *
 * Priority order follows
 * https://gitlab.com/gitlab-org/editor-extensions/gitlab-lsp/-/work_items/2653:
 * review feedback, then failing pipeline, then merge conflicts (author only).
 */
const mergeRequestPrompts = ({ mergeRequest, isAuthor }: MergeRequestWithRole): WelcomePrompt[] => {
  const prompts: WelcomePrompt[] = [];
  if (hasUnresolvedDiscussions(mergeRequest)) {
    prompts.push(contextualPrompt('Address review feedback on ', '!', mergeRequest));
  }
  if (mergeRequest.headPipeline?.status === 'FAILED') {
    prompts.push(contextualPrompt('Fix failing pipeline on ', '!', mergeRequest));
  }
  if (mergeRequest.conflicts && isAuthor) {
    prompts.push(contextualPrompt('Resolve merge conflicts in ', '!', mergeRequest));
  }
  return prompts;
};

/**
 * Flattens per-item prompt lists so every work item contributes one prompt
 * before any item contributes a second. A repeat prompt about a real work item
 * beats a generic filler, but only once distinct items run out.
 */
const preferDistinctItems = (perItem: WelcomePrompt[][]): WelcomePrompt[] => [
  ...perItem
    .map(([topPrompt]) => topPrompt)
    .filter((prompt): prompt is WelcomePrompt => prompt !== undefined),
  ...perItem.flatMap((prompts) => prompts.slice(1)),
];

/**
 * An MR present in both the authored and assigned lists keeps
 * `isAuthor: true` so the author-only conflict heuristic still applies.
 */
const mergeAndDedupe = (
  authored: ContextualMergeRequest[],
  assigned: ContextualMergeRequest[],
): MergeRequestWithRole[] => {
  const byIid = new Map<string, MergeRequestWithRole>();
  for (const mergeRequest of authored) {
    byIid.set(mergeRequest.iid, { mergeRequest, isAuthor: true });
  }
  for (const mergeRequest of assigned) {
    if (!byIid.has(mergeRequest.iid)) {
      byIid.set(mergeRequest.iid, { mergeRequest, isAuthor: false });
    }
  }
  return [...byIid.values()].sort((a, b) => byUpdatedAtDesc(a.mergeRequest, b.mergeRequest));
};

const buildMergeRequestPrompts = (
  authored: ContextualMergeRequest[],
  assigned: ContextualMergeRequest[],
): WelcomePrompt[] =>
  preferDistinctItems(mergeAndDedupe(authored, assigned).map(mergeRequestPrompts)).slice(
    0,
    PROMPTS_PER_CATEGORY,
  );

const recentIssues = (issues: ContextualIssue[]): ContextualIssue[] =>
  issues
    .filter((issue) => Date.now() - Date.parse(issue.updatedAt) <= RECENT_ACTIVITY_WINDOW_MS)
    .sort(byUpdatedAtDesc);

/**
 * Issues that qualify for the "start" prompts before the linked-MR check:
 * recently active with no closing merge requests. `Issue.mergeRequestsCount`
 * only counts MRs that close the issue, so the caller should verify the top
 * candidates for *related* MRs (one request per issue — the GraphQL field is
 * capped to one issue per request) and pass the positives to
 * {@link buildIssueCategory} as `issuesWithLinkedMrs`.
 */
export const startPromptCandidateIssues = (
  data: GetUserContextualWorkItemsData,
): ContextualIssue[] =>
  recentIssues(data.project?.issues.nodes ?? []).filter((issue) => issue.mergeRequestsCount === 0);

/**
 * Contextual prompts for assigned issues, in priority order:
 *
 * 1. "Start an implementation plan" / 2. "Start a branch" — recently active
 *    issues without linked merge requests (closing MRs via
 *    `mergeRequestsCount`, related MRs via `issuesWithLinkedMrs`). The two
 *    prompt kinds are spread across the first two qualifying issues for
 *    variety.
 * 3. "Summarize discussion" — discussion-heavy issues. V1 approximates the
 *    spec's "3+ comments from others since the last description update" with
 *    a plain comment-count threshold; the query has no per-note authorship
 *    or description-version data (issue #2653, open question 3).
 * 4. "Summarize discussion" again, for an issue that already has a "start"
 *    prompt — only to fill slots that would otherwise go to a generic.
 *
 * The two "start" kinds are alternative ways into the same work, so they are
 * spread across two issues and never both offered for one issue.
 *
 * Every heuristic requires recent activity: a stale issue makes a poor
 * "kick off your work" suggestion regardless of its comment volume.
 */
const buildIssuePrompts = (
  issues: ContextualIssue[],
  issuesWithLinkedMrs: ReadonlySet<string>,
): WelcomePrompt[] => {
  const prompts: WelcomePrompt[] = [];
  const usedIids = new Set<string>();

  const sorted = recentIssues(issues);
  const actionable = sorted.filter(
    (issue) => issue.mergeRequestsCount === 0 && !issuesWithLinkedMrs.has(issue.iid),
  );
  if (actionable[0]) {
    prompts.push(contextualPrompt('Start an implementation plan for ', '#', actionable[0]));
    usedIids.add(actionable[0].iid);
  }
  if (actionable[1]) {
    prompts.push(contextualPrompt('Start a branch for ', '#', actionable[1]));
    usedIids.add(actionable[1].iid);
  }

  const summarize = (issue: ContextualIssue): WelcomePrompt =>
    contextualPrompt('Summarize the discussion on ', '#', issue, ' and update the description');
  const discussionHeavy = sorted.filter(
    (issue) => issue.userNotesCount >= DISCUSSION_HEAVY_NOTES_THRESHOLD,
  );
  prompts.push(...discussionHeavy.filter((issue) => !usedIids.has(issue.iid)).map(summarize));
  prompts.push(...discussionHeavy.filter((issue) => usedIids.has(issue.iid)).map(summarize));

  return prompts.slice(0, PROMPTS_PER_CATEGORY);
};

/** Fisher–Yates shuffle. */
const shuffled = <T>(items: T[]): T[] => {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

/**
 * The generics are sampled in random order so the padding varies between
 * sessions instead of always repeating the first entries of the default list.
 */
const padWithGeneric = (contextual: WelcomePrompt[], generic: WelcomePrompt[]): WelcomePrompt[] =>
  [...contextual, ...shuffled(generic)].slice(0, PROMPTS_PER_CATEGORY);

/**
 * The "Your MRs" welcome-card category: contextual prompts derived from the
 * user's open MRs, padded with (shuffled) generic defaults. Resolvable from
 * the main query alone — no follow-up requests — so the MR tab can be shown
 * before the issue tab.
 */
export const buildMergeRequestCategory = (
  data: GetUserContextualWorkItemsData,
): WelcomePromptCategory => {
  const [mergeRequestDefaults] = DEFAULT_WELCOME_CATEGORIES;
  const contextual = data.project
    ? buildMergeRequestPrompts(
        data.project.authoredMergeRequests.nodes,
        data.project.assignedMergeRequests.nodes,
      )
    : [];
  return {
    label: mergeRequestDefaults.label,
    examples: padWithGeneric(contextual, mergeRequestDefaults.examples),
  };
};

/**
 * The "Issues assigned to you" welcome-card category: contextual prompts
 * derived from the user's assigned issues, padded with (shuffled) generic
 * defaults.
 *
 * `issuesWithLinkedMrs` — iids of issues known to have related merge requests
 * (see {@link startPromptCandidateIssues}); they are excluded from the "start"
 * prompts because work on them has evidently begun.
 */
export const buildIssueCategory = (
  data: GetUserContextualWorkItemsData,
  issuesWithLinkedMrs: ReadonlySet<string> = new Set(),
): WelcomePromptCategory => {
  const [, issueDefaults] = DEFAULT_WELCOME_CATEGORIES;
  const contextual = data.project
    ? buildIssuePrompts(data.project.issues.nodes, issuesWithLinkedMrs)
    : [];
  return {
    label: issueDefaults.label,
    examples: padWithGeneric(contextual, issueDefaults.examples),
  };
};
